import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ModalController, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { CustomFormComponent } from '@services/formBuilder/abstract-custom-form-field';
import { WorkScheduleService } from '@services/work-schedule/work-schedule.service';
import { UserService } from '@services/user/user.service';
import {
  ScheduleEditModalComponent,
  ScheduleEditModalConfig,
  ScheduleEditModalResult
} from '@modules/management/pages/details/work-schedule/modals/schedule-edit-modal/schedule-edit-modal.component';
import { FolderLocationInfo, MultiLocationScheduleConfig } from '@services/work-schedule/model';

/**
 * Component that displays an embedded calendar preview with an "Edit Schedule" button.
 * Used in folder and asset forms to show the effective schedule and allow editing.
 *
 * Schedule selection changes are saved immediately when the modal's Save button is clicked.
 * The parent form does not need to handle saving these values.
 */
@Component({
  selector: 'app-schedule-display',
  templateUrl: './schedule-display.component.html',
  styleUrls: ['./schedule-display.component.scss'],
  standalone: false
})
export class ScheduleDisplayComponent extends CustomFormComponent implements OnInit, OnChanges {
  // Subject info
  @Input() subjectType: 'folder' | 'asset' | 'team' = 'folder';
  @Input() subjectID: number = 0;
  @Input() subjectName: string = '';

  // Locations for multi-location subjects (teams pass this directly, folders load it)
  @Input() locations: number[] = [];

  // Current schedule selections
  @Input() workDaysScheduleID: number = -1;
  @Input() daysOffScheduleID: number = -1;

  // Inherited values for display in the modal
  @Input() inheritedWorkDaysScheduleID: number = 0;
  @Input() inheritedWorkDaysName: string = '';
  @Input() inheritedDaysOffScheduleID: number = 0;
  @Input() inheritedDaysOffName: string = '';
  @Input() inheritLabel: string = '';

  // Effective schedule IDs (resolved through inheritance) for calendar display
  @Input() effectiveWorkDaysScheduleID: number = 0;
  @Input() effectiveDaysOffScheduleID: number = 0;

  // Disabled state
  @Input() disabled: boolean = false;

  // Legend label overrides (for different contexts like assets vs workers)
  // Pass translation keys - component will translate them
  @Input() workDaysLabelKey: string = 'WORK_SCHEDULE.Work_Days';
  @Input() daysOffLabelKey: string = 'WORK_SCHEDULE.Days_Off';

  // Multi-location schedule support
  @Input() multiLocationConfig: MultiLocationScheduleConfig;  // Current per-location schedule config

  // Output when schedule selections change (for parent form tracking)
  @Output() scheduleChanged = new EventEmitter<{
    workDaysScheduleID: number;
    daysOffScheduleID: number;
  }>();

  // Required by CustomFormComponent
  @Output() onChanged: EventEmitter<any> = new EventEmitter<any>();
  public required: boolean = false;

  // Local state for calendar
  calendarWorkDaysID: number = 0;
  calendarDaysOffID: number = 0;

  // Saving state
  isSaving: boolean = false;

  // Multi-location state
  folderLocations: FolderLocationInfo[] = [];
  isMultiLocationFolder: boolean = false;
  isLoadingLocations: boolean = false;

  constructor(
    private modalController: ModalController,
    private toastController: ToastController,
    private translate: TranslateService,
    private workScheduleService: WorkScheduleService,
    private userService: UserService
  ) {
    super();
  }

  async ngOnInit() {
    // If effective IDs are not provided, we need to compute them
    // This can happen when schedules weren't loaded yet when the parent computed the IDs
    if (!this.effectiveWorkDaysScheduleID && !this.effectiveDaysOffScheduleID) {
      await this.loadAndComputeEffectiveSchedules();
    } else {
      this.updateCalendarIDs();
    }

    this.loadSubjectLocations();
  }

  /**
   * Load work schedules and compute effective schedule IDs for teams
   * This handles the case where schedules weren't loaded when the parent computed inherited IDs
   */
  private async loadAndComputeEffectiveSchedules(): Promise<void> {
    if (this.subjectType !== 'team') {
      // For non-team subjects, use provided values
      this.updateCalendarIDs();
      return;
    }

    try {
      // Ensure work schedules are loaded
      await this.workScheduleService.refreshWorkSchedules();

      // For single-location teams, compute inherited schedules from location defaults
      if (this.locations && this.locations.length === 1) {
        const locationID = this.locations[0];
        const locationDefaults = this.workScheduleService.getLocationDefaultSchedules(locationID);

        // Update inherited IDs
        this.inheritedWorkDaysScheduleID = locationDefaults.workDaysScheduleID || 0;
        this.inheritedDaysOffScheduleID = locationDefaults.daysOffScheduleID || 0;
        this.inheritedWorkDaysName = locationDefaults.workDaysScheduleName || '';
        this.inheritedDaysOffName = locationDefaults.daysOffScheduleName || '';

        // If no multiLocationConfig, use inherited as effective
        if (!this.multiLocationConfig) {
          this.effectiveWorkDaysScheduleID = this.inheritedWorkDaysScheduleID;
          this.effectiveDaysOffScheduleID = this.inheritedDaysOffScheduleID;
        } else if (this.multiLocationConfig.mode === 'per-location') {
          // Get location-specific schedule if exists
          const locationConfig = this.multiLocationConfig.locations?.[locationID];
          if (locationConfig) {
            this.effectiveWorkDaysScheduleID = locationConfig.workDaysScheduleID > 0
              ? locationConfig.workDaysScheduleID
              : this.inheritedWorkDaysScheduleID;
            this.effectiveDaysOffScheduleID = locationConfig.daysOffScheduleID > 0
              ? locationConfig.daysOffScheduleID
              : this.inheritedDaysOffScheduleID;
          } else {
            // Fall back to global or inherited
            this.effectiveWorkDaysScheduleID = this.multiLocationConfig.global?.workDaysScheduleID || this.inheritedWorkDaysScheduleID;
            this.effectiveDaysOffScheduleID = this.multiLocationConfig.global?.daysOffScheduleID || this.inheritedDaysOffScheduleID;
          }
        } else {
          // Single mode - check for direct properties or global
          const config = this.multiLocationConfig as any;
          const workDaysID = config.workDaysScheduleID ?? config.global?.workDaysScheduleID ?? 0;
          const daysOffID = config.daysOffScheduleID ?? config.global?.daysOffScheduleID ?? 0;
          this.effectiveWorkDaysScheduleID = workDaysID > 0 ? workDaysID : this.inheritedWorkDaysScheduleID;
          this.effectiveDaysOffScheduleID = daysOffID > 0 ? daysOffID : this.inheritedDaysOffScheduleID;
        }
      } else {
        // Multi-location team or no locations - use subscriber defaults
        const subscriberDefaults = this.workScheduleService.getDefaultScheduleIDs();
        this.inheritedWorkDaysScheduleID = subscriberDefaults.workDaysScheduleID || 0;
        this.inheritedDaysOffScheduleID = subscriberDefaults.daysOffScheduleID || 0;

        if (!this.multiLocationConfig) {
          this.effectiveWorkDaysScheduleID = this.inheritedWorkDaysScheduleID;
          this.effectiveDaysOffScheduleID = this.inheritedDaysOffScheduleID;
        } else if (this.multiLocationConfig.mode === 'per-location') {
          // Use global settings for multi-location preview
          this.effectiveWorkDaysScheduleID = this.multiLocationConfig.global?.workDaysScheduleID || this.inheritedWorkDaysScheduleID;
          this.effectiveDaysOffScheduleID = this.multiLocationConfig.global?.daysOffScheduleID || this.inheritedDaysOffScheduleID;
        } else {
          const config = this.multiLocationConfig as any;
          const workDaysID = config.workDaysScheduleID ?? config.global?.workDaysScheduleID ?? 0;
          const daysOffID = config.daysOffScheduleID ?? config.global?.daysOffScheduleID ?? 0;
          this.effectiveWorkDaysScheduleID = workDaysID > 0 ? workDaysID : this.inheritedWorkDaysScheduleID;
          this.effectiveDaysOffScheduleID = daysOffID > 0 ? daysOffID : this.inheritedDaysOffScheduleID;
        }
      }

      this.updateCalendarIDs();
    } catch (error) {
      console.error('[ScheduleDisplay] Error loading schedules:', error);
      this.updateCalendarIDs();
    }
  }

  /**
   * Load locations to detect multi-location subjects (folders/teams)
   */
  private async loadSubjectLocations(): Promise<void> {
    // Skip for assets
    if (this.subjectType === 'asset' || this.subjectID <= 0) {
      return;
    }

    this.isLoadingLocations = true;
    try {
      if (this.subjectType === 'team') {
        // For teams, locations are passed as an input (array of location IDs)
        // Convert to FolderLocationInfo format
        if (this.locations && this.locations.length > 0) {
          this.folderLocations = this.locations.map(locationID => {
            const location = this.userService.getLocation(locationID);
            return {
              locationID: locationID,
              locationName: location ? location.name : `Location ${locationID}`,
              assetCount: 0  // Teams don't have asset counts
            };
          });
          this.isMultiLocationFolder = this.folderLocations.length > 1;
        } else {
          // Empty locations means "all locations" - treat as single (global) schedule
          this.folderLocations = [];
          this.isMultiLocationFolder = false;
        }
      } else {
        // For folders, load locations from the service
        this.folderLocations = await this.workScheduleService.getFolderLocations(this.subjectID);
        this.isMultiLocationFolder = this.folderLocations.length > 1;
      }
    } catch (error) {
      console.error('Failed to load subject locations:', error);
      this.folderLocations = [];
      this.isMultiLocationFolder = false;
    } finally {
      this.isLoadingLocations = false;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['effectiveWorkDaysScheduleID']) {
    }
    if (changes['effectiveDaysOffScheduleID']) {
    }
    if (changes['effectiveWorkDaysScheduleID'] || changes['effectiveDaysOffScheduleID'] ||
        changes['workDaysScheduleID'] || changes['daysOffScheduleID']) {
      this.updateCalendarIDs();
    }
    // Reload locations if subject ID or locations input changes
    if ((changes['subjectID'] && !changes['subjectID'].firstChange) ||
        (changes['locations'] && !changes['locations'].firstChange)) {
      this.loadSubjectLocations();
    }
  }

  private updateCalendarIDs() {
    // Use effective IDs for calendar display
    this.calendarWorkDaysID = this.effectiveWorkDaysScheduleID || 0;
    this.calendarDaysOffID = this.effectiveDaysOffScheduleID || 0;
  }

  async openEditModal() {
    const config: ScheduleEditModalConfig = {
      subjectType: this.subjectType,
      subjectID: this.subjectID,
      subjectName: this.subjectName,
      workDaysScheduleID: this.workDaysScheduleID,
      daysOffScheduleID: this.daysOffScheduleID,
      inheritedWorkDaysScheduleID: this.inheritedWorkDaysScheduleID,
      inheritedWorkDaysName: this.inheritedWorkDaysName,
      inheritedDaysOffScheduleID: this.inheritedDaysOffScheduleID,
      inheritedDaysOffName: this.inheritedDaysOffName,
      effectiveWorkDaysScheduleID: this.effectiveWorkDaysScheduleID,
      effectiveDaysOffScheduleID: this.effectiveDaysOffScheduleID,
      inheritLabel: this.inheritLabel || this.translate.instant('WORK_SCHEDULE.Inherited'),
      // Multi-location support
      multiLocationMode: this.isMultiLocationFolder,
      locations: this.folderLocations,
      multiLocationConfig: this.multiLocationConfig
    };

    const modal = await this.modalController.create({
      component: ScheduleEditModalComponent,
      componentProps: { config },
      cssClass: 'fullscreen-modal'
    });

    await modal.present();

    const { data, role } = await modal.onDidDismiss<ScheduleEditModalResult>();

    if (role === 'save' && data?.changed) {
      if (data.multiLocationConfig) {
        // Save per-location configuration
        await this.saveMultiLocationSchedule(data.multiLocationConfig);
      } else {
        // Save single schedule configuration
        await this.saveScheduleSelection(data.workDaysScheduleID, data.daysOffScheduleID);
      }
    }
  }

  /**
   * Save multi-location schedule configuration
   */
  private async saveMultiLocationSchedule(config: MultiLocationScheduleConfig): Promise<void> {
    if (this.subjectID <= 0 || (this.subjectType !== 'folder' && this.subjectType !== 'team')) {
      return;
    }

    this.isSaving = true;

    try {
      if (this.subjectType === 'team') {
        await this.workScheduleService.saveTeamMultiLocationSchedule(
          this.subjectID,
          config.global,
          config.locations
        );
      } else {
        await this.workScheduleService.saveFolderMultiLocationSchedule(
          this.subjectID,
          config.global,
          config.locations
        );
      }

      // Update local state
      this.multiLocationConfig = config;

      // Update calendar IDs based on the saved config
      // For single-location subjects, use the location-specific schedule if set, otherwise global
      if (this.locations && this.locations.length === 1) {
        const locationID = this.locations[0];
        const locationConfig = config.locations?.[locationID];
        if (locationConfig) {
          const workDaysID = locationConfig.workDaysScheduleID > 0 ? locationConfig.workDaysScheduleID : config.global.workDaysScheduleID;
          const daysOffID = locationConfig.daysOffScheduleID > 0 ? locationConfig.daysOffScheduleID : config.global.daysOffScheduleID;
          this.updateEffectiveScheduleIDs(workDaysID, daysOffID);
        } else {
          this.updateEffectiveScheduleIDs(config.global.workDaysScheduleID, config.global.daysOffScheduleID);
        }
      } else {
        // Multi-location - use global for preview
        this.updateEffectiveScheduleIDs(config.global.workDaysScheduleID, config.global.daysOffScheduleID);
      }

      // Emit change event
      this.scheduleChanged.emit({
        workDaysScheduleID: config.global.workDaysScheduleID,
        daysOffScheduleID: config.global.daysOffScheduleID
      });

      this.onChanged.emit({ multiLocationConfig: config });

      // Show success toast
      const toast = await this.toastController.create({
        message: this.translate.instant('WORK_SCHEDULE.Schedule_Saved'),
        duration: 2000,
        color: 'success',
        position: 'bottom'
      });
      await toast.present();

    } catch (error) {
      console.error('Failed to save multi-location schedule:', error);

      const toast = await this.toastController.create({
        message: this.translate.instant('SHARED.Error_Saving'),
        duration: 3000,
        color: 'danger',
        position: 'bottom'
      });
      await toast.present();

    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Save schedule selection to the backend and update local state
   */
  private async saveScheduleSelection(newWorkDaysID: number, newDaysOffID: number): Promise<void> {
    if (this.subjectID <= 0) {
      // Can't save for new (unsaved) subjects
      return;
    }

    this.isSaving = true;

    try {
      // Call appropriate save method based on subject type
      if (this.subjectType === 'folder') {
        await this.workScheduleService.saveFolderScheduleSelection(
          this.subjectID,
          newWorkDaysID,
          newDaysOffID
        );
      } else if (this.subjectType === 'team') {
        await this.workScheduleService.saveTeamScheduleSelection(
          this.subjectID,
          newWorkDaysID,
          newDaysOffID
        );
      } else {
        await this.workScheduleService.saveAssetScheduleSelection(
          this.subjectID,
          newWorkDaysID,
          newDaysOffID
        );
      }

      // Update local state
      this.workDaysScheduleID = newWorkDaysID;
      this.daysOffScheduleID = newDaysOffID;

      // Recalculate effective IDs for calendar display
      this.updateEffectiveScheduleIDs(newWorkDaysID, newDaysOffID);

      // Emit change events for parent form tracking
      this.scheduleChanged.emit({
        workDaysScheduleID: newWorkDaysID,
        daysOffScheduleID: newDaysOffID
      });

      this.onChanged.emit({
        workDaysScheduleID: newWorkDaysID,
        daysOffScheduleID: newDaysOffID
      });

      // Show success toast
      const toast = await this.toastController.create({
        message: this.translate.instant('WORK_SCHEDULE.Schedule_Saved'),
        duration: 2000,
        color: 'success',
        position: 'bottom'
      });
      await toast.present();

    } catch (error) {
      console.error('Failed to save schedule selection:', error);

      // Show error toast
      const toast = await this.toastController.create({
        message: this.translate.instant('SHARED.Error_Saving'),
        duration: 3000,
        color: 'danger',
        position: 'bottom'
      });
      await toast.present();

    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Recalculate effective schedule IDs after a selection change
   */
  private updateEffectiveScheduleIDs(workDaysID: number, daysOffID: number): void {
    // Work days: use selected if > 0, otherwise inherited
    if (workDaysID > 0) {
      this.calendarWorkDaysID = workDaysID;
    } else if (this.inheritedWorkDaysScheduleID > 0) {
      this.calendarWorkDaysID = this.inheritedWorkDaysScheduleID;
    } else {
      // Keep current effective ID (from initial load)
      this.calendarWorkDaysID = this.effectiveWorkDaysScheduleID || 0;
    }

    // Days off: use selected if > 0, otherwise inherited
    if (daysOffID > 0) {
      this.calendarDaysOffID = daysOffID;
    } else if (this.inheritedDaysOffScheduleID > 0) {
      this.calendarDaysOffID = this.inheritedDaysOffScheduleID;
    } else {
      // Keep current effective ID (from initial load)
      this.calendarDaysOffID = this.effectiveDaysOffScheduleID || 0;
    }

  }
}
